/**
 * Entry flow for Review merge from ONE invoice (SPEC-invoice-merge-history-2026-10-02 R4 AC1):
 * choose the invoice to combine it with, then review. Used by the invoice detail page and the list
 * row action; selecting two list rows opens the review directly.
 *
 * Candidate selection is selection only. The invoice list is queried with the anchor's own project,
 * so the choices are same-project invoices; every state is offered (the server plan decides what is
 * allowed and why). A number that was already merged away can be searched: choosing it resolves to the
 * invoice it now lives on, never the retired record.
 */
import { useEffect, useState } from 'react';
import { useInvoices } from '@/hooks';
import type { MergeApplyResult } from '@/lib/invoice-merge-plan';
import { mergeStateLabel, formatMergeMoney } from '@/lib/invoice-merge-guidance';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InvoiceMergeReview } from './InvoiceMergeReview';

export interface MergeAnchorInvoice {
  id: string;
  invoice_number: string;
  project_id: string;
  project_name?: string | null;
}

export interface InvoiceMergeCandidatePickerProps {
  anchor: MergeAnchorInvoice;
  onChoose: (invoiceId: string) => void;
  onClose: () => void;
}

export function InvoiceMergeCandidatePicker({ anchor, onChoose, onClose }: InvoiceMergeCandidatePickerProps) {
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const { data, isLoading, error } = useInvoices({
    project_id: anchor.project_id,
    search: search || undefined,
    sort_by: 'invoice_number',
    sort_order: 'desc',
    limit: 50,
  });
  const rows = (data?.invoices ?? []).filter((inv) => inv.id !== anchor.id);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-h-[92dvh] w-[calc(100vw-1rem)] max-w-xl overflow-y-auto p-4 sm:p-6"
        data-testid="invoice-merge-picker"
      >
        <DialogHeader>
          <DialogTitle>Combine invoice {anchor.invoice_number} with another invoice</DialogTitle>
          <DialogDescription>
            Invoices on {anchor.project_name || 'the same project'} only. You can search by an original invoice number, including a
            number that was already merged into another invoice. Nothing changes until you review and confirm.
          </DialogDescription>
        </DialogHeader>
        <label className="space-y-1 text-sm">
          <span className="font-medium">Search by invoice number</span>
          <input
            className="w-full rounded border p-2"
            placeholder="e.g. 0757"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="merge-picker-search"
          />
        </label>
        {isLoading && <p role="status" aria-busy="true">Loading invoices…</p>}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            Could not load invoices: {(error as Error).message}. Nothing was changed.
          </p>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="merge-picker-empty">
            No other invoices on this project{search ? ` match “${search}”` : ''}.
          </p>
        )}
        <ul className="space-y-2" aria-label="Invoices on this project">
          {rows.map((inv) => {
            const retired = !!inv.merged_into_invoice_id;
            const target = retired ? inv.merged_into_invoice_id! : inv.id;
            const alreadyTogether = retired && target === anchor.id;
            return (
              <li key={inv.id}>
                <button
                  type="button"
                  className="w-full rounded border p-3 text-left text-sm hover:bg-muted focus-visible:outline focus-visible:outline-2 disabled:opacity-60"
                  data-testid={`merge-candidate-${inv.invoice_number}`}
                  disabled={alreadyTogether}
                  onClick={() => onChoose(target)}
                >
                  <span className="font-medium">Invoice {inv.invoice_number}</span> · {mergeStateLabel(inv.status)} ·{' '}
                  {formatMergeMoney(Number(inv.total_amount ?? 0).toFixed(2))}
                  {inv.client_name ? ` · ${inv.client_name}` : ''}
                  {retired && (
                    <span className="block text-muted-foreground" data-testid={`merge-candidate-merged-${inv.invoice_number}`}>
                      {alreadyTogether
                        ? `Already part of invoice ${anchor.invoice_number}.`
                        : `Merged into invoice ${inv.merged_into_invoice_number ?? 'another invoice'}. Choosing it reviews that invoice.`}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="merge-picker-cancel">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export interface InvoiceMergeFlowProps {
  anchor: MergeAnchorInvoice;
  onClose: () => void;
  onMerged?: (result: MergeApplyResult) => void;
  onCancelInvoice?: (invoiceId: string) => void;
  notice?: string | null;
}

export function InvoiceMergeFlow({ anchor, onClose, onMerged, onCancelInvoice, notice }: InvoiceMergeFlowProps) {
  const [otherId, setOtherId] = useState<string | null>(null);
  if (!otherId) {
    return <InvoiceMergeCandidatePicker anchor={anchor} onChoose={setOtherId} onClose={onClose} />;
  }
  return (
    <InvoiceMergeReview
      invoiceIdA={anchor.id}
      invoiceIdB={otherId}
      onClose={onClose}
      onBack={() => setOtherId(null)}
      onMerged={onMerged}
      onCancelInvoice={onCancelInvoice}
      notice={notice}
    />
  );
}
